/**
 * InboundEmailTab — "Leads por correo" (R1: RECIBIR Y VER).
 *
 * Muestra la dirección única de la empresa (para configurar el auto-forward desde la
 * bandeja donde caen los correos de Lamudi / Inmuebles24 / Vivanuncios / EasyBroker),
 * las instrucciones de reenvío, y la bandeja de correos recibidos.
 *
 * ⚠️ R1 NO crea leads y NO extrae datos: solo confirma que los correos LLEGAN. La
 * extracción es R2 y la creación del lead es R3. La UI lo dice explícitamente para que
 * nadie espere leads todavía.
 *
 * Endpoints: GET /inbound-email/mailbox · POST /inbound-email/mailbox/rotate ·
 *            GET /inbound-email/emails
 * Molde: ApiKeysManager.jsx / UtmBuilder.jsx (mismos Card + toast + api).
 */
import { useCallback, useEffect, useState } from 'react';
import { APP_NAME } from '@/lib/brand';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Mail, Copy, RefreshCw, Inbox, AlertTriangle, Info, ShieldCheck, GitBranch, Trash2, Plus, Eye, Zap } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const PAGE_SIZE = 25;

// R4-A.1 — el botón "Crear lead" aparece si hay ALGO convertible: portal detectado (regla) o
// cualquier dato de contacto/identidad extraído (por regla O por IA). Sin esto, un correo que
// la IA extrajo con teléfono/email pero sin nombre —o sin portal— quedaría con datos y SIN
// botón (callejón sin salida). El endpoint no exige portal: crea el lead al admin si no hay mapeo.
const hasConvertibleData = (e) =>
  !!(e.detected_portal || e.parsed_fields?.name || e.parsed_fields?.phone || e.parsed_fields?.email);

const FORWARD_STEPS = {
  gmail: [
    'Entra a Gmail con la cuenta donde te llegan los correos de los portales.',
    'Ve a Configuración (⚙️) → "Ver toda la configuración" → pestaña "Reenvío y correo POP/IMAP".',
    'Haz clic en "Añadir una dirección de reenvío" y pega la dirección de arriba.',
    'Gmail enviará un código de confirmación a esa dirección. Avísanos para dártelo — todavía no podemos mostrártelo solos.',
    'Una vez confirmado, crea un filtro (Buscar → Crear filtro) que reenvíe SOLO los correos de los portales, no todo tu buzón.',
  ],
  outlook: [
    'Entra a Outlook con la cuenta donde te llegan los correos de los portales.',
    'Ve a Configuración (⚙️) → "Correo" → "Reglas".',
    'Crea una regla nueva: condición "El remitente contiene" (ej. lamudi.com), acción "Redirigir a".',
    'Pega la dirección de arriba como destino y guarda.',
    'Outlook no pide código de confirmación: los correos deberían empezar a llegar de inmediato.',
  ],
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
}

// R5-C — VISOR del correo original. El html es contenido NO CONFIABLE (lo escribió un
// desconocido) → se muestra SIEMPRE en un iframe sandbox="" (sin scripts, sin acceso al padre,
// origen opaco). El CSP inline bloquea recursos remotos por defecto.
const VIEWER_RENDER_CAP = 1_000_000;  // >1MB → NO auto-renderiza el iframe (evita trabar el navegador); el html llega COMPLETO igual

function buildSrcDoc(html, showImages) {
  // img-src data: (default) bloquea pixeles de rastreo + fuga de IP. "Cargar imágenes" → data: https:
  const imgSrc = showImages ? 'data: https:' : 'data:';
  const csp = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src ${imgSrc}; style-src 'unsafe-inline'; font-src data:; media-src data:">`;
  return csp + (html || '');
}

function htmlToPlainText(html) {
  // DOMParser NO ejecuta scripts ni carga recursos (documento inerte) → texto seguro.
  try {
    return new DOMParser().parseFromString(html || '', 'text/html').body.textContent.trim();
  } catch {
    return '';
  }
}

export default function InboundEmailTab() {
  const { user, impersonating } = useAuth();
  // R7-B — quién puede ENCENDER: superadmin directo O superadmin SUPLANTANDO. Bajo suplantación
  // user.role es "admin" (auth §4, verificado C1/C4), pero `impersonating` (AuthContext) lo marca.
  const canEnable = user?.role === 'superadmin' || impersonating;
  const [mailbox, setMailbox] = useState(null);
  const [loadingMailbox, setLoadingMailbox] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [savingMaster, setSavingMaster] = useState(false);   // R7-B: master de creación automática por empresa
  const [provider, setProvider] = useState('gmail');

  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loadingEmails, setLoadingEmails] = useState(true);

  // R5-C — visor del correo original (modal con iframe blindado).
  const [viewer, setViewer] = useState(null);            // el detalle del correo, o null (modal cerrado)
  const [viewerLoading, setViewerLoading] = useState(false);
  const [showImages, setShowImages] = useState(false);   // opt-in EXPLÍCITO de imágenes remotas (avisa al remitente)
  const [forceBigRender, setForceBigRender] = useState(false);  // previsualizar un html pesado (>1MB) bajo demanda

  const [campaigns, setCampaigns] = useState([]);   // dropdown de campañas (routing rules)

  // R6.1 — reglas de ruteo por TEXTO (cuerpo) → campaña. (R7-C retiró el mapeo de portal.)
  const [routingRules, setRoutingRules] = useState([]);
  const [routingText, setRoutingText] = useState('');
  const [routingMode, setRoutingMode] = useState('exact');   // "exact" (default) | "contains"
  const [routingCampaign, setRoutingCampaign] = useState('');
  const [savingRouting, setSavingRouting] = useState(false);
  const [togglingRuleId, setTogglingRuleId] = useState('');  // R7-C: toggle auto_create por regla
  const [disablingAll, setDisablingAll] = useState(false);   // R7-C: "Apagar todas"

  const loadMailbox = useCallback(() => {
    setLoadingMailbox(true);
    api.get('/inbound-email/mailbox')
      .then(r => setMailbox(r.data))
      .catch(e => {
        setMailbox(null);
        toast.error(e.response?.data?.detail || 'No se pudo cargar tu dirección de correo');
      })
      .finally(() => setLoadingMailbox(false));
  }, []);

  // R7-B — master de creación automática POR EMPRESA. El backend gatea ENCENDER a superadmin
  // (transición); el admin del cliente solo llega aquí para APAGAR. NO cambia la condición (R7-C).
  const setAutoCreateMaster = async (enabled) => {
    // C8 — confirmación SOLO al ENCENDER (acción irreversible: los correos que coincidan con una
    // regla se vuelven lead SOLOS, con avisos y —si aplica— llamada al contacto). Apagar NO confirma.
    if (enabled && !window.confirm(
      'Vas a ACTIVAR la creación automática de leads de esta empresa.\n\n' +
      'Los correos que coincidan con una regla de ruteo se convertirán en lead SOLOS, con avisos y ' +
      '—si aplica— llamada al contacto.\n\n¿Continuar?'
    )) return;
    setSavingMaster(true);
    try {
      await api.post('/inbound-email/auto-create-master', { enabled });
      toast.success(enabled ? 'Creación automática ACTIVADA' : 'Creación automática apagada');
      loadMailbox();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar');
    } finally {
      setSavingMaster(false);
    }
  };

  const loadEmails = useCallback((nextSkip = 0) => {
    setLoadingEmails(true);
    api.get('/inbound-email/emails', { params: { limit: PAGE_SIZE, skip: nextSkip } })
      .then(r => {
        setEmails(r.data?.items || []);
        setTotal(r.data?.total || 0);
        setSkip(nextSkip);
      })
      .catch(() => { setEmails([]); setTotal(0); })
      .finally(() => setLoadingEmails(false));
  }, []);

  const loadRoutingRules = useCallback(() => {
    api.get('/inbound-email/routing-rules')
      .then(r => setRoutingRules(r.data?.items || []))
      .catch(() => setRoutingRules([]));
  }, []);

  useEffect(() => {
    loadMailbox(); loadEmails(0); loadRoutingRules();
    // Dropdown de campañas: eje correcto (§4) y endpoint ya existente.
    api.get('/campaigns').then(r => setCampaigns(Array.isArray(r.data) ? r.data : [])).catch(() => setCampaigns([]));
  }, [loadMailbox, loadEmails, loadRoutingRules]);

  // R6.1 — alta de regla texto→campaña. El backend devuelve 409 con mensaje claro si choca el
  // índice UNIQUE {company_id, field, mode, text_norm} (regla duplicada) → se muestra tal cual.
  const saveRoutingRule = async () => {
    const text = routingText.trim();
    if (!text || !routingCampaign) {
      toast.error('Escribe el texto y elige una campaña');
      return;
    }
    setSavingRouting(true);
    try {
      await api.post('/inbound-email/routing-rules', { text, mode: routingMode, campaign_id: routingCampaign });
      toast.success('Regla creada');
      // El modo por defecto (Idéntico) es una decisión de producto: lo SEGURO por defecto. Se
      // reinicia tras cada alta para que la siguiente regla no nazca permisiva ("Contiene") sin
      // que nadie lo elija — dando de alta 9 seguidas, una quedaría en el modo equivocado y no
      // matchearía nunca, sin señal.
      setRoutingText(''); setRoutingCampaign(''); setRoutingMode('exact');
      loadRoutingRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear la regla');
    } finally {
      setSavingRouting(false);
    }
  };

  const deleteRoutingRule = async (rule) => {
    if (!window.confirm(`¿Quitar la regla "${rule.text}"?`)) return;
    try {
      await api.delete(`/inbound-email/routing-rules/${rule.id}`);
      toast.success('Regla eliminada');
      loadRoutingRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  // R7-C (Candado 2) — toggle auto_create POR REGLA. El backend gatea ENCENDER a superadmin/
  // suplantación (transición); el admin del cliente solo llega aquí para APAGAR. Confirmación
  // SOLO al encender (efecto irreversible), igual que el master de empresa (C8).
  const toggleRoutingAutoCreate = async (rule, enabled) => {
    if (enabled && !window.confirm(
      'Vas a ACTIVAR la creación automática para esta regla.\n\n' +
      'Los correos que coincidan con “' + rule.text + '” se convertirán en lead SOLOS (con la ' +
      'empresa también encendida), con avisos y —si aplica— llamada al contacto.\n\n¿Continuar?'
    )) return;
    setTogglingRuleId(rule.id);
    try {
      await api.patch(`/inbound-email/routing-rules/${rule.id}/auto-create`, { enabled });
      toast.success(enabled ? 'Auto-creación ACTIVADA para la regla' : 'Auto-creación apagada para la regla');
      loadRoutingRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar');
    } finally {
      setTogglingRuleId('');
    }
  };

  // R7-D — toggle auto_create_web POR REGLA (canal IA: correos que ningún portal reconoció y la IA
  // logró leer — formulario web y otros formatos). Flag SEPARADO de auto_create (Portales). Mismo
  // gate asimétrico + confirmación al encender. SOFT (L1d): sin llave de IA el canal AVISA (banner +
  // ⚠) pero el superadmin PUEDE encender igual — no auto-creará hasta que se configure la llave.
  const toggleRoutingAutoCreateWeb = async (rule, enabled) => {
    if (enabled && !window.confirm(
      'Vas a ACTIVAR la creación automática por IA para esta regla.\n\n' +
      'Los correos que NINGÚN portal reconoció y la IA logró leer (tu formulario web y otros ' +
      'formatos) que coincidan con “' + rule.text + '” se convertirán en lead SOLOS (con la empresa ' +
      'también encendida), con avisos y —si aplica— llamada al contacto.\n\n¿Continuar?'
    )) return;
    setTogglingRuleId(rule.id);
    try {
      await api.patch(`/inbound-email/routing-rules/${rule.id}/auto-create-web`, { enabled });
      toast.success(enabled ? 'Auto-creación por IA ACTIVADA para la regla' : 'Auto-creación por IA apagada');
      loadRoutingRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar');
    } finally {
      setTogglingRuleId('');
    }
  };

  // R7-C — "Apagar todas": el admin apaga el auto de TODAS sus reglas de golpe (apagar es SEGURO;
  // NO hay "encender todas" — encender es por regla y superadmin, §3 #13).
  const disableAllRoutingAutoCreate = async () => {
    if (!window.confirm('¿Apagar la creación automática de TODAS las reglas?')) return;
    setDisablingAll(true);
    try {
      const r = await api.post('/inbound-email/routing-rules/auto-create/disable-all');
      toast.success(`Auto-creación apagada en ${r.data?.disabled_count ?? 0} regla(s)`);
      loadRoutingRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo apagar');
    } finally {
      setDisablingAll(false);
    }
  };

  const campaignName = (id) => campaigns.find(c => c.id === id)?.name || id;

  // R3b — botón "Crear lead" por fila. Idempotente en backend; aquí solo bloqueo el botón
  // mientras corre y refresco la bandeja con el resultado.
  const [creatingId, setCreatingId] = useState('');
  const createLead = async (em) => {
    setCreatingId(em.id);
    try {
      const r = await api.post(`/inbound-email/emails/${em.id}/create-lead`);
      const d = r.data || {};
      if (d.status === 'created') {
        toast.success(d.awaiting
          ? 'Lead creado — en espera de horario de su equipo'
          : `Lead creado${d.assigned_name ? ` — asignado a ${d.assigned_name}` : ''}`);
        if (d.development_conflict) toast.warning('El correo y la campaña indican desarrollos distintos: revisa el conflicto.');
      } else if (d.status === 'already_converted') {
        toast.info('Este correo ya se había convertido en lead.');
      } else if (d.status === 'duplicate') {
        toast.info('Este correo es un duplicado de otro que ya se convirtió en lead — no se creó uno nuevo.');
      } else if (d.status === 'cap_exceeded') {
        toast.error('Cupo mensual de leads excedido: no se creó el lead.');
      }
      loadEmails(skip);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear el lead');
    } finally {
      setCreatingId('');
    }
  };

  // R5-C — abrir el visor: trae el detalle (html/text/headers) por el endpoint dedicado
  // (la lista no manda html). Resetea el opt-in de imágenes y el gate de tamaño en cada apertura.
  const openViewer = async (id) => {
    setViewerLoading(true);
    setShowImages(false);
    setForceBigRender(false);
    try {
      const r = await api.get(`/inbound-email/emails/${id}`);
      setViewer(r.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo abrir el correo');
    } finally {
      setViewerLoading(false);
    }
  };

  const copy = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Dirección copiada al portapapeles');
    } catch {
      toast.error('No se pudo copiar. Selecciónala y cópiala a mano.');
    }
  };

  const rotate = async () => {
    if (!window.confirm(
      '¿Generar una dirección nueva?\n\nLa dirección actual dejará de recibir correos y tendrás que ' +
      'actualizar el reenvío en Gmail/Outlook. Los correos que ya recibiste NO se borran.'
    )) return;
    setRotating(true);
    try {
      const r = await api.post('/inbound-email/mailbox/rotate');
      setMailbox(r.data);
      toast.success('Dirección nueva generada — actualiza el reenvío en tu correo');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo generar la dirección nueva');
    } finally {
      setRotating(false);
    }
  };

  const address = mailbox?.address || '';
  const domainMissing = mailbox && !mailbox.domain_configured;

  return (
    <div className="space-y-5" data-testid="inbound-email-tab">
      {/* Aviso de alcance: R1 no crea leads. Que nadie los espere. */}
      <div className="flex items-start gap-3 rounded-lg border border-amber-400/25 bg-amber-400/5 p-3">
        <Info className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-100/80 leading-relaxed">
          <strong className="text-amber-200">Recepción + creación con botón.</strong> {APP_NAME} recibe los
          correos, extrae sus datos y tú creas cada lead con el botón “Crear lead”. La creación
          <strong> automática</strong> (sin botón) se activa con DOS interruptores: el de la empresa (arriba) y el
          de cada regla de ruteo (abajo), y <strong>solo la enciende el equipo de Dealider</strong>: al quedar
          ambos encendidos, los correos que coincidan con esa regla se vuelven lead <strong>solos</strong> y
          disparan lo que su campaña tenga configurado (avisos y, si la campaña lo incluye, llamada).
        </p>
      </div>

      {/* R7-B — MASTER de creación automática POR EMPRESA. Arriba y FUERA de la card "Portales → Campañas"
          (que se retira en R7-C). Opción 3: cliente ve estado + botón "Apagar"; superadmin ve Switch. */}
      <Card className="bg-[#1F2833]/60 border-white/5">
        <CardContent className="p-4 flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-1 max-w-2xl">
            <p className="text-sm text-white font-medium flex items-center gap-2">
              <Zap className="w-4 h-4 text-violet-300" /> Creación automática de leads
            </p>
            {mailbox?.auto_create_enabled ? (
              <p className="text-xs text-muted-foreground leading-relaxed">
                <strong className="text-emerald-300">Activada.</strong> Los correos que coincidan con una regla de
                ruteo se convierten en lead solos y disparan lo que su campaña tenga configurado (avisos y, si
                aplica, llamada).
              </p>
            ) : (
              <p className="text-xs text-muted-foreground leading-relaxed">
                <strong className="text-amber-300">Apagada.</strong> Los correos se quedan en la bandeja hasta que
                creas el lead con el botón.{canEnable ? '' : ' Para activarla, escríbele al equipo de Dealider.'}
              </p>
            )}
            {canEnable && (
              <p className="text-[10px] text-violet-300/70 leading-snug">
                Control del equipo de Dealider — enciende ESTA empresa una a la vez y monitorea.
              </p>
            )}
          </div>
          {canEnable ? (
            <Switch
              checked={!!mailbox?.auto_create_enabled}
              disabled={savingMaster || loadingMailbox}
              onCheckedChange={(v) => setAutoCreateMaster(v)}
              data-testid="auto-create-master"
            />
          ) : mailbox?.auto_create_enabled ? (
            <Button
              size="sm"
              variant="outline"
              disabled={savingMaster}
              className="h-8 text-xs border-amber-400/30 text-amber-300 hover:bg-amber-400/10"
              onClick={() => setAutoCreateMaster(false)}
              data-testid="auto-create-master-off"
            >
              Apagar
            </Button>
          ) : (
            <span className="text-[10px] text-amber-300/70 flex items-center gap-1 shrink-0">🔒 Solo Dealider</span>
          )}
        </CardContent>
      </Card>

      {/* Dirección del buzón */}
      <Card className="bg-[#1F2833]/60 border-white/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Mail className="w-4 h-4 text-cyan-300" /> Tu dirección para leads
          </CardTitle>
          <CardDescription className="text-xs">
            Reenvía a esta dirección los correos que te mandan los portales (Lamudi, Inmuebles24,
            Vivanuncios, EasyBroker). Es única de tu empresa: no la compartas públicamente.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {loadingMailbox ? (
            <p className="text-xs text-muted-foreground">Cargando…</p>
          ) : domainMissing ? (
            <div className="flex items-start gap-2 rounded-lg border border-red-400/30 bg-red-400/5 p-3">
              <AlertTriangle className="w-4 h-4 text-red-300 mt-0.5 shrink-0" />
              <p className="text-xs text-red-100/80">
                El dominio de recepción todavía no está configurado en el servidor. Avísale al equipo de
                {' '}{APP_NAME} — no hay nada que puedas hacer desde aquí.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <code
                  className="flex-1 min-w-[260px] text-xs sm:text-sm text-cyan-200 bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 break-all"
                  data-testid="inbound-email-address"
                >
                  {address}
                </code>
                <Button size="sm" variant="outline" onClick={() => copy(address)} data-testid="inbound-email-copy">
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copiar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={rotate}
                  disabled={rotating}
                  className="text-muted-foreground hover:text-amber-300"
                  data-testid="inbound-email-rotate"
                >
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${rotating ? 'animate-spin' : ''}`} />
                  Generar otra
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Correos recibidos en esta dirección: <strong className="text-white">{mailbox?.received_count ?? 0}</strong>
                {mailbox?.last_received_at ? ` · último: ${fmtDate(mailbox.last_received_at)}` : ''}
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Instrucciones de auto-forward */}
      <Card className="bg-[#1F2833]/60 border-white/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-violet-300" /> Cómo configurar el reenvío automático
          </CardTitle>
          <CardDescription className="text-xs">
            Configúralo una sola vez en el correo donde HOY te llegan los avisos de los portales.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            {['gmail', 'outlook'].map(p => (
              <Button
                key={p}
                size="sm"
                variant={provider === p ? 'default' : 'outline'}
                onClick={() => setProvider(p)}
                className="text-xs capitalize"
                data-testid={`inbound-email-provider-${p}`}
              >
                {p}
              </Button>
            ))}
          </div>
          <ol className="space-y-2 list-decimal list-inside">
            {FORWARD_STEPS[provider].map((step, i) => (
              <li key={i} className="text-xs text-muted-foreground leading-relaxed">{step}</li>
            ))}
          </ol>
          {provider === 'gmail' && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-400/25 bg-amber-400/5 p-3">
              <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
              <p className="text-[11px] text-amber-100/80 leading-relaxed">
                <strong className="text-amber-200">Ojo con Gmail:</strong> pide un código de confirmación que
                envía a esta dirección. Ese correo llegará a la bandeja de abajo, pero por ahora el código
                <strong> te lo tenemos que dar nosotros a mano</strong>. Escríbenos y te lo pasamos.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* R6.1 — Reglas por texto → campaña. (R7-C retiró la card "Portales → Campañas": la campaña
          viene SOLO de una regla, y el auto se enciende POR REGLA con el Switch de cada una.) */}
      <Card className="bg-[#1F2833]/60 border-white/5">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base text-white flex items-center gap-2">
                <GitBranch className="w-4 h-4 text-sky-300" /> Reglas por texto → Campañas
              </CardTitle>
              <CardDescription className="text-xs mt-1">
                Si el correo dice tal cosa, mándalo a tal campaña. Útil cuando un mismo portal trae leads
                de varios desarrollos o modelos. Con el interruptor de la empresa y el de la regla encendidos,
                esos correos se vuelven lead solos; si no, esperan al botón “Crear lead”.
              </CardDescription>
            </div>
            {routingRules.some(r => r.auto_create || r.auto_create_web) && (
              <Button
                size="sm"
                variant="outline"
                disabled={disablingAll}
                className="h-7 text-[10px] border-amber-400/30 text-amber-300 hover:bg-amber-400/10 shrink-0"
                onClick={disableAllRoutingAutoCreate}
                data-testid="routing-rules-disable-all"
              >
                Apagar todas
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* R7-D (D13i) — banner "IA sin configurar": el canal IA/web NO puede auto-crear sin llave. */}
          {mailbox && !mailbox.ai_configured && (
            <p className="text-[11px] text-amber-200/90 bg-amber-400/5 border border-amber-400/25 rounded-md px-3 py-2 flex items-start gap-1.5" data-testid="ai-not-configured-banner">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-300" />
              <span><strong className="text-amber-200">IA sin configurar.</strong> El canal “IA” (correos del formulario de tu sitio y otros formatos que la IA interpreta) necesita una llave de IA. Esta empresa no la tiene → esos correos no se pueden auto-crear. Configúrala en Ajustes o escríbenos.</span>
            </p>
          )}
          <p className="text-[11px] text-sky-200/80 bg-sky-400/5 border border-sky-400/15 rounded-md px-3 py-2 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Tip: abre el correo con “Ver correo”, copia el renglón tal cual y pégalo aquí — así evitas
              errores de tecleo (por ejemplo, este cliente escribe <span className="text-white/90">“3 rec”</span>,
              no “3 recámaras”).</span>
          </p>

          {/* Orden del flujo: dónde · qué texto · cómo comparar · a qué campaña → [Crear regla].
              El control del MODO va DENTRO de la fila, ANTES del botón: si quedara después, el
              usuario pulsa sin elegirlo y toda regla nace "idéntico" (Proppit nunca matchearía). */}
          <div className="flex items-end gap-2 flex-wrap">
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Buscar en</p>
              <select
                disabled
                value="body"
                className="w-40 h-9 text-xs rounded-md bg-black/20 border border-white/10 text-white/70 px-2 cursor-not-allowed"
                data-testid="routing-rule-field"
              >
                <option value="body">Cuerpo del correo</option>
              </select>
            </div>
            <div className="space-y-1 flex-1 min-w-[200px]">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Texto</p>
              <Input
                value={routingText}
                onChange={e => setRoutingText(e.target.value)}
                placeholder="Fresno - 116 m2 - 3 rec"
                className="h-9 text-xs"
                data-testid="routing-rule-text"
              />
            </div>
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Cómo comparar</p>
              <div className="flex items-center gap-2 h-9 px-2 rounded-md bg-black/20 border border-white/10">
                <span className={`text-xs ${routingMode === 'exact' ? 'text-white' : 'text-muted-foreground'}`}>Idéntico</span>
                <Switch
                  checked={routingMode === 'contains'}
                  onCheckedChange={v => setRoutingMode(v ? 'contains' : 'exact')}
                  data-testid="routing-rule-mode"
                />
                <span className={`text-xs ${routingMode === 'contains' ? 'text-white' : 'text-muted-foreground'}`}>Contiene</span>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Campaña</p>
              <select
                value={routingCampaign}
                onChange={e => setRoutingCampaign(e.target.value)}
                className="w-64 h-9 text-xs rounded-md bg-black/30 border border-white/10 text-white px-2"
                data-testid="routing-rule-campaign"
              >
                <option value="">— elige campaña —</option>
                {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <Button size="sm" onClick={saveRoutingRule} disabled={savingRouting} data-testid="routing-rule-save">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Crear regla
            </Button>
          </div>

          {/* Texto de AYUDA de los 2 modos (el control vive arriba, en la fila) — el modo cambia
              DÓNDE se busca, no solo el rigor. */}
          <div className="text-[10px] text-muted-foreground leading-snug bg-white/[0.03] border border-white/5 rounded-md px-3 py-2">
            <p><span className="text-white/80">Idéntico</span> — el renglón completo debe ser exactamente este texto (para formularios).</p>
            <p><span className="text-white/80">Contiene</span> — el texto aparece en cualquier parte del correo (para portales).</p>
          </div>

          {routingRules.length > 0 && (
            <ul className="space-y-1.5" data-testid="routing-rules-list">
              {routingRules.map(r => (
                <li key={r.id} className="text-xs bg-white/[0.03] border border-white/5 rounded-md px-3 py-2 flex items-center justify-between gap-2">
                  <span className="text-white/90 truncate min-w-0">
                    <Badge variant="outline" className="text-[10px] mr-2 border-sky-400/40 text-sky-300">
                      {r.mode === 'contains' ? 'contiene' : 'idéntico'}
                    </Badge>
                    “{r.text}” → {campaignName(r.campaign_id)}
                  </span>
                  <div className="flex items-center gap-3 shrink-0">
                    {/* R7-C — canal PORTALES (auto_create). Encender = superadmin/suplantación
                        (canEnable); el cliente ve estado + candado y solo APAGA si está ON. */}
                    {canEnable ? (
                      <label className="flex items-center gap-1" title="Auto para correos de PORTALES (Lamudi, Proppit…) que coinciden con esta regla">
                        <span className={`text-[10px] ${r.auto_create ? 'text-emerald-300' : 'text-muted-foreground'}`}>{r.auto_create ? 'Portales ON' : 'Portales'}</span>
                        <Switch checked={!!r.auto_create} disabled={togglingRuleId === r.id} onCheckedChange={(v) => toggleRoutingAutoCreate(r, v)} data-testid={`routing-rule-autocreate-${r.id}`} />
                      </label>
                    ) : r.auto_create ? (
                      <Button size="sm" variant="outline" disabled={togglingRuleId === r.id} className="h-6 text-[10px] border-amber-400/30 text-amber-300 hover:bg-amber-400/10" onClick={() => toggleRoutingAutoCreate(r, false)} data-testid={`routing-rule-autocreate-off-${r.id}`}>Portales ON · Apagar</Button>
                    ) : (
                      <span className="text-[10px] text-muted-foreground/60 whitespace-nowrap" title="Solo el equipo de Dealider puede encender el automático">🔒 Portales</span>
                    )}
                    {/* R7-D — canal IA/web (auto_create_web). SOFT (L1): el superadmin puede encender
                        aunque falte la llave (el banner + el ⚠ avisan; sin llave no auto-creará hasta
                        configurarla) — un candado basado en una detección que puede ERRAR no debe dejar
                        la función inalcanzable. El cliente ve estado + candado. */}
                    {canEnable ? (
                      <label className="flex items-center gap-1" title={mailbox?.ai_configured ? 'Auto para correos que la IA interpretó (tu formulario web y otros formatos no reconocidos) y coinciden con esta regla' : '⚠️ Sin llave de IA: aunque lo enciendas, NO auto-creará hasta configurar la llave'}>
                        <span className={`text-[10px] ${r.auto_create_web ? 'text-violet-300' : 'text-muted-foreground'}`}>{r.auto_create_web ? 'IA ON' : 'IA'}{!mailbox?.ai_configured ? ' ⚠' : ''}</span>
                        <Switch checked={!!r.auto_create_web} disabled={togglingRuleId === r.id} onCheckedChange={(v) => toggleRoutingAutoCreateWeb(r, v)} data-testid={`routing-rule-autocreateweb-${r.id}`} />
                      </label>
                    ) : r.auto_create_web ? (
                      <Button size="sm" variant="outline" disabled={togglingRuleId === r.id} className="h-6 text-[10px] border-violet-400/30 text-violet-300 hover:bg-violet-400/10" onClick={() => toggleRoutingAutoCreateWeb(r, false)} data-testid={`routing-rule-autocreateweb-off-${r.id}`}>IA ON · Apagar</Button>
                    ) : !mailbox?.ai_configured ? (
                      <span className="text-[10px] text-muted-foreground/40 whitespace-nowrap" title="Necesita una llave de IA configurada">🔒 IA (sin llave)</span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground/60 whitespace-nowrap" title="Solo el equipo de Dealider puede encender el automático">🔒 IA</span>
                    )}
                    <button
                      onClick={() => deleteRoutingRule(r)}
                      className="text-muted-foreground hover:text-red-400 shrink-0"
                      data-testid={`routing-rule-delete-${r.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Bandeja */}
      <Card className="bg-[#1F2833]/60 border-white/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Inbox className="w-4 h-4 text-emerald-300" /> Correos recibidos
            {total > 0 && <Badge variant="outline" className="ml-1 text-[10px]">{total}</Badge>}
          </CardTitle>
          <CardDescription className="text-xs">
            Si aquí aparece un correo, el reenvío funciona. Todavía no se convierten en leads.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingEmails ? (
            <p className="text-xs text-muted-foreground py-6 text-center">Cargando…</p>
          ) : emails.length === 0 ? (
            <div className="text-center py-10">
              <Inbox className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Todavía no llega ningún correo.</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Configura el reenvío de arriba y mándate una prueba desde tu correo.
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="inbound-email-list">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-white/5">
                      <th className="py-2 pr-3 font-medium">Fecha</th>
                      <th className="py-2 pr-3 font-medium">Remitente</th>
                      <th className="py-2 pr-3 font-medium">Asunto</th>
                      <th className="py-2 pr-3 font-medium">Detectado</th>
                      <th className="py-2 pr-3 font-medium">Ruteo</th>
                      <th className="py-2 pr-3 font-medium">Estado</th>
                      <th className="py-2 font-medium">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emails.map(e => (
                      <tr key={e.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                        <td className="py-2.5 pr-3 text-muted-foreground whitespace-nowrap">{fmtDate(e.received_at)}</td>
                        <td className="py-2.5 pr-3 text-white/90">
                          {e.from_name || e.from_email || '—'}
                          {e.from_name && e.from_email && (
                            <span className="block text-[10px] text-muted-foreground">{e.from_email}</span>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 max-w-[320px]">
                          <button
                            onClick={() => openViewer(e.id)}
                            className="text-left text-white/70 hover:text-white flex items-center gap-1 max-w-full"
                            title="Ver correo original"
                            data-testid={`view-email-${e.id}`}
                          >
                            <Eye className="w-3 h-3 shrink-0 text-muted-foreground" />
                            <span className="truncate">{e.subject || '(sin asunto)'}</span>
                          </button>
                        </td>
                        <td className="py-2.5 pr-3 max-w-[260px]">
                          {(e.detected_portal || e.parsed_by === 'ai') ? (
                            <div className="space-y-0.5">
                              {e.detected_portal ? (
                                <Badge variant="outline" className="text-[10px] border-amber-400/40 text-amber-300">
                                  {e.detected_portal}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] border-violet-400/40 text-violet-300">
                                  Extraído por IA
                                </Badge>
                              )}
                              {(e.parsed_fields?.name || e.parsed_fields?.phone) && (
                                <span className="block text-[10px] text-white/80 truncate">
                                  {[e.parsed_fields?.name, e.parsed_fields?.phone].filter(Boolean).join(' · ')}
                                </span>
                              )}
                              {e.parsed_fields?.development_name && (
                                <span className="block text-[10px] text-emerald-300/90 truncate">
                                  🏘 {e.parsed_fields.development_name}
                                </span>
                              )}
                              {(e.parsed_fields?.development_candidates?.length > 1) && (
                                <span className="block text-[10px] text-amber-300/90">
                                  ⚠ {e.parsed_fields.development_candidates.length} desarrollos posibles
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-[10px] text-muted-foreground/60">—</span>
                          )}
                        </td>
                        {/* R6.3 — Ruteo (separado de Detectado: extracción vs ruteo fallan por
                            motivos distintos). Convertidos usan el sello histórico; no-convertidos,
                            el cálculo vivo contra las reglas actuales. */}
                        <td className="py-2.5 pr-3 max-w-[220px]">
                          {e.routing?.status === 'routed' ? (
                            <div className="space-y-0.5">
                              <span className="block text-[11px] text-emerald-300 truncate">
                                → {campaigns.find(c => c.id === e.routing.campaign_id)?.name || 'campaña eliminada'}
                              </span>
                              {e.routing.matched_text && (
                                <span className="block text-[10px] text-muted-foreground truncate" title={e.routing.matched_text}>
                                  coincidió: “{e.routing.matched_text}”
                                </span>
                              )}
                            </div>
                          ) : e.routing?.status === 'unmatched' ? (
                            <Badge variant="outline" className="text-[10px] border-white/15 text-muted-foreground">Sin regla</Badge>
                          ) : e.routing?.status === 'conflict' ? (
                            <Badge variant="outline" className="text-[10px] border-amber-400/40 text-amber-300">Reglas en conflicto</Badge>
                          ) : e.routing?.status === 'pre_rules' ? (
                            <span className="text-[10px] text-muted-foreground/60 italic">convertido antes de las reglas</span>
                          ) : (
                            <span className="text-[10px] text-muted-foreground/60">—</span>
                          )}
                        </td>
                        <td className="py-2.5">
                          {e.converted_lead_id ? (
                            e.lead_created_by === 'auto' ? (
                              <Badge variant="outline" className="text-[10px] border-violet-400/40 text-violet-300">Lead creado · Auto</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] border-sky-400/40 text-sky-300">Lead creado</Badge>
                            )
                          ) : e.status === 'cap_exceeded' ? (
                            <Badge variant="outline" className="text-[10px] border-red-400/40 text-red-300">Cupo excedido</Badge>
                          ) : e.status === 'duplicate_of_lead' ? (
                            <Badge variant="outline" className="text-[10px] border-amber-400/40 text-amber-300">Repetido de otro correo</Badge>
                          ) : e.status === 'duplicate' ? (
                            <Badge variant="outline" className="text-[10px] border-amber-400/40 text-amber-300">Repetido</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] border-emerald-400/40 text-emerald-300">Recibido</Badge>
                          )}
                        </td>
                        <td className="py-2.5">
                          {e.converted_lead_id ? (
                            <span className="text-[10px] text-sky-300/80">✓ convertido</span>
                          ) : e.status === 'duplicate_of_lead' ? (
                            <span className="text-[10px] text-amber-300/70">↳ ya creado por otro correo</span>
                          ) : hasConvertibleData(e) ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-[10px] border-emerald-400/30 text-emerald-300 hover:bg-emerald-400/10"
                              disabled={creatingId === e.id}
                              onClick={() => createLead(e)}
                              data-testid={`create-lead-${e.id}`}
                            >
                              {creatingId === e.id ? 'Creando…' : 'Crear lead'}
                            </Button>
                          ) : (
                            <span className="text-[10px] text-muted-foreground/50">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {total > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={skip === 0}
                    onClick={() => loadEmails(Math.max(0, skip - PAGE_SIZE))}
                  >
                    Anteriores
                  </Button>
                  <span className="text-[11px] text-muted-foreground">
                    {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} de {total}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={skip + PAGE_SIZE >= total}
                    onClick={() => loadEmails(skip + PAGE_SIZE)}
                  >
                    Siguientes
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* R5-C — VISOR del correo original: iframe sandbox="" (ni allow-scripts ni allow-same-origin). */}
      <Dialog open={!!viewer || viewerLoading} onOpenChange={(o) => { if (!o) { setViewer(null); setViewerLoading(false); } }}>
        <DialogContent className="max-w-4xl w-[92vw] max-h-[92vh] overflow-y-auto bg-[#0F1720] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-sm text-white flex items-center gap-2">
              <Mail className="w-4 h-4 text-amber-300" /> Correo original
            </DialogTitle>
          </DialogHeader>
          {viewerLoading && !viewer && (
            <div className="py-10 text-center text-xs text-muted-foreground">Cargando…</div>
          )}
          {viewer && (() => {
            const bigHtml = (viewer.html_bytes || 0) > VIEWER_RENDER_CAP;
            const plain = (viewer.text && viewer.text.trim()) ? viewer.text : htmlToPlainText(viewer.html);
            const fmtBytes = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);
            return (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-white/80">
                  <div className="col-span-2"><span className="text-muted-foreground">De:</span> {viewer.from_name || ''} &lt;{viewer.from_email || '—'}&gt;</div>
                  <div className="col-span-2"><span className="text-muted-foreground">Asunto:</span> {viewer.subject || '(sin asunto)'}</div>
                  <div><span className="text-muted-foreground">Fecha:</span> {fmtDate(viewer.received_at)}</div>
                  <div><span className="text-muted-foreground">spam_score:</span> {viewer.spam_score ?? '—'}</div>
                  <div className="col-span-2"><span className="text-muted-foreground">Portal:</span> {viewer.detected_portal || '—'} · <span className="text-muted-foreground">extraído por:</span> {viewer.parsed_by || '—'}</div>
                </div>

                <Tabs defaultValue="html" className="w-full">
                  <TabsList className="bg-[#1F2833]/80 border border-white/5">
                    <TabsTrigger value="html" className="text-[11px]">HTML original</TabsTrigger>
                    <TabsTrigger value="text" className="text-[11px]">Texto plano</TabsTrigger>
                    <TabsTrigger value="evidence" className="text-[11px]">Evidencia</TabsTrigger>
                  </TabsList>

                  <TabsContent value="html" className="mt-2">
                    {!showImages ? (
                      <div className="flex items-center justify-between gap-3 mb-2 rounded-md border border-amber-400/30 bg-amber-400/5 px-3 py-1.5">
                        <span className="text-[10px] text-amber-200/90">
                          🔒 Imágenes remotas bloqueadas por privacidad. Cargarlas le <b>AVISA al remitente</b> que abriste el correo (fecha, hora e IP).
                        </span>
                        <Button size="sm" variant="outline" className="h-6 text-[10px] shrink-0 border-amber-400/40 text-amber-200"
                          onClick={() => setShowImages(true)} data-testid="viewer-load-images">
                          Cargar imágenes de todos modos
                        </Button>
                      </div>
                    ) : (
                      <div className="mb-2 rounded-md border border-white/10 bg-white/[0.03] px-3 py-1.5">
                        <span className="text-[10px] text-muted-foreground">⚠️ Imágenes cargadas — el remitente pudo registrar esta apertura.</span>
                      </div>
                    )}
                    {(bigHtml && !forceBigRender) ? (
                      <div className="h-[40vh] flex flex-col items-center justify-center gap-2 text-center rounded-md border border-white/10 bg-white/[0.02]">
                        <span className="text-[11px] text-amber-200/90">Correo muy pesado ({fmtBytes(viewer.html_bytes)}) — previsualizar puede tardar.</span>
                        <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => setForceBigRender(true)}>Previsualizar de todos modos</Button>
                        <span className="text-[10px] text-muted-foreground">La pestaña “Texto plano” funciona sin previsualizar.</span>
                      </div>
                    ) : (
                      <iframe
                        key={showImages ? 'img-on' : 'img-off'}
                        title="Correo original"
                        sandbox=""
                        srcDoc={buildSrcDoc(viewer.html, showImages)}
                        referrerPolicy="no-referrer"
                        className="w-full h-[60vh] bg-white rounded-md border border-white/10"
                      />
                    )}
                  </TabsContent>

                  <TabsContent value="text" className="mt-2">
                    <pre className="w-full h-[60vh] overflow-auto whitespace-pre-wrap break-words text-[11px] text-white/85 bg-black/30 rounded-md border border-white/10 p-3">
                      {plain || '(sin contenido de texto)'}
                    </pre>
                  </TabsContent>

                  <TabsContent value="evidence" className="mt-2">
                    <div className="space-y-1.5 text-[11px] text-white/80 font-mono bg-black/30 rounded-md border border-white/10 p-3 max-h-[60vh] overflow-auto">
                      <div><span className="text-muted-foreground">message_id:</span> {viewer.message_id || '—'}</div>
                      <div><span className="text-muted-foreground">orig_date:</span> {viewer.orig_date || '—'}</div>
                      <div><span className="text-muted-foreground">spf:</span> {viewer.spf || '—'}</div>
                      <div><span className="text-muted-foreground">dkim:</span> {viewer.dkim || '—'}</div>
                      <div><span className="text-muted-foreground">sender_ip:</span> {viewer.sender_ip || '—'}</div>
                      <div className="break-all"><span className="text-muted-foreground">envelope:</span> {viewer.envelope_raw || '—'}</div>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
